/**
 * Turning react-hook-form's nested error object into a flat list.
 *
 * A long form can refuse to submit because of a field the user cannot see — one
 * section card down a scrolling dialog, or a fee below the fold. Marking that
 * field red is necessary but not sufficient: the form also has to say out loud
 * what is holding the save up. Every screen needs the same list to do that, so
 * it is built once here.
 */

export interface FlatFieldError {
  /** Dotted path as react-hook-form names it: `sections.0.minSeats`. */
  path: string;
  message: string;
}

/** Keys react-hook-form adds to an error node that are not themselves errors. */
const NOT_AN_ERROR = new Set(['ref', 'type', 'types']);

export function flattenFieldErrors(errors: unknown, prefix = ''): FlatFieldError[] {
  if (!errors || typeof errors !== 'object') return [];

  const collected: FlatFieldError[] = [];

  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    if (NOT_AN_ERROR.has(key) || !value || typeof value !== 'object') continue;

    const path = prefix ? `${prefix}.${key}` : key;
    const { message } = value as { message?: unknown };

    if (typeof message === 'string' && message.length > 0) {
      collected.push({ path, message });
      continue;
    }

    // An array field holds its own issues under `root` and its items' issues
    // under numeric keys — both are reached by walking on.
    collected.push(...flattenFieldErrors(value, path));
  }

  return collected;
}
